import { useSyncExternalStore } from "react";
import { ConnectProto } from "@racoon/api";

/**
 * ModalProvider renders `body` and `footer` as two sibling nodes captured at
 * `openModal()` time, so they can't share React state (no common parent, no way
 * to re-render stored options). The picker's selection therefore lives in a tiny
 * external store created per opened modal and read by both halves.
 */
export interface JobPickerSelectionStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => ReadonlySet<string>;
  toggle: (id: string, job?: ConnectProto.Job) => void;
  toggleMany: (ids: string[], select: boolean, jobs?: ConnectProto.Job[]) => void;
  clear: () => void;
  /**
   * The picked jobs themselves, not just their ids. The caller's own job query
   * is usually narrower than the picker's (the operator can clear the truck and
   * category filters), so it cannot be relied on to resolve what was picked.
   */
  getSelectedJobs: () => ConnectProto.Job[];
}

export function createJobPickerSelection(multiple: boolean): JobPickerSelectionStore {
  let selected: ReadonlySet<string> = new Set<string>();
  const listeners = new Set<() => void>();
  // Accumulates every job the operator has seen selected, so changing a filter
  // mid-pick cannot make an already selected job unresolvable.
  const knownJobs = new Map<string, ConnectProto.Job>();

  const remember = (jobs?: ConnectProto.Job[]) => {
    jobs?.forEach((job) => knownJobs.set(job.id.toString(), job));
  };

  const commit = (next: ReadonlySet<string>) => {
    selected = next;
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => selected,
    toggle: (id, job) => {
      remember(job ? [job] : undefined);
      if (!multiple) {
        commit(selected.has(id) ? new Set() : new Set([id]));
        return;
      }
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      commit(next);
    },
    toggleMany: (ids, select, jobs) => {
      remember(jobs);
      const next = new Set(selected);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      commit(next);
    },
    clear: () => commit(new Set()),
    getSelectedJobs: () =>
      [...selected]
        .map((id) => knownJobs.get(id))
        .filter((job): job is ConnectProto.Job => Boolean(job)),
  };
}

export const useJobPickerSelection = (store: JobPickerSelectionStore) =>
  useSyncExternalStore(store.subscribe, store.getSnapshot);
